/**
 * NuevaCargaDialog — Formulario móvil-first para registrar una carga de combustible.
 * Flujo:
 *   1. Piloto elige embarcación (pre-seleccionada si tiene 1 sola)
 *   2. Toma foto de la factura → sube → Claude Vision extrae datos
 *   3. Confirma / edita los campos autocompletados
 *   4. Guarda
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Box, Typography, Grid, CircularProgress,
  Alert, Chip,
} from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const initial = {
  embarcacion_id: '',
  estacion_id: '',
  fecha_carga: '',
  galones: '',
  precio_por_galon: '',
  total_pagado: '',
  tipo_combustible: 'gasolina',
  referencia_ticket: '',
  factura_url: '',
  notas: '',
};

export default function NuevaCargaDialog({ open, embarcaciones, estaciones, empresaId, onClose, onSuccess, onError }) {
  const [form, setForm] = useState(initial);
  const [subiendo, setSubiendo] = useState(false);
  const [analizando, setAnalizando] = useState(false);
  const [ocr, setOcr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [facturaNombre, setFacturaNombre] = useState('');

  useEffect(() => {
    if (open) {
      // Si el piloto solo tiene 1 embarcación, la pre-selecciono
      const preselect = embarcaciones && embarcaciones.length === 1
        ? { embarcacion_id: embarcaciones[0].id } : {};
      setForm({ ...initial, ...preselect });
      setOcr(null);
      setFacturaNombre('');
    }
  }, [open, embarcaciones]);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  // Auto-calcular total si tenemos galones + precio
  useEffect(() => {
    const g = Number(form.galones), p = Number(form.precio_por_galon);
    if (g > 0 && p > 0) {
      const total = Math.round(g * p);
      if (Math.abs(Number(form.total_pagado || 0) - total) > 1) {
        // Solo auto-actualiza si el usuario no lo editó a algo distinto
        setForm(prev => ({ ...prev, total_pagado: String(total) }));
      }
    }
    // eslint-disable-next-line
  }, [form.galones, form.precio_por_galon]);

  const subirFactura = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const t = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        body: fd,
      });
      if (!r.ok) throw new Error('Error subiendo');
      const d = await r.json();
      setForm(prev => ({ ...prev, factura_url: d.url }));
      setFacturaNombre(file.name);
      // Disparar OCR
      analizar(d.url);
    } catch (err) {
      onError?.('Error al subir la factura');
    } finally { setSubiendo(false); }
  };

  const analizar = async (url) => {
    setAnalizando(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/combustible/analizar-factura`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ url, empresa_id: empresaId }),
      });
      const d = await r.json();
      setOcr(d);
      if (d.ok) {
        // Prellenar campos con lo extraído (solo si están vacíos)
        setForm(prev => ({
          ...prev,
          galones: prev.galones || (d.galones != null ? String(d.galones) : ''),
          precio_por_galon: prev.precio_por_galon || (d.precio_por_galon != null ? String(d.precio_por_galon) : ''),
          total_pagado: prev.total_pagado || (d.total_pagado != null ? String(d.total_pagado) : ''),
          tipo_combustible: d.tipo_combustible || prev.tipo_combustible,
          estacion_id: prev.estacion_id || (d.estacion_id_match || ''),
          referencia_ticket: prev.referencia_ticket || (d.numero_ticket || ''),
          fecha_carga: prev.fecha_carga || (d.fecha_ticket || ''),
        }));
      }
    } catch (e) {
      setOcr({ ok: false, error: 'No se pudo analizar' });
    } finally { setAnalizando(false); }
  };

  const puedeGuardar = form.embarcacion_id
    && Number(form.galones) > 0
    && Number(form.precio_por_galon) > 0
    && Number(form.total_pagado) > 0;

  const guardar = async () => {
    if (!puedeGuardar || saving) return;
    setSaving(true);
    try {
      const body = {
        empresa_id: empresaId,
        embarcacion_id: Number(form.embarcacion_id),
        estacion_id: form.estacion_id ? Number(form.estacion_id) : null,
        fecha_carga: form.fecha_carga || null,
        galones: Number(form.galones),
        precio_por_galon: Number(form.precio_por_galon),
        total_pagado: Number(form.total_pagado),
        tipo_combustible: form.tipo_combustible,
        referencia_ticket: form.referencia_ticket.trim() || null,
        factura_url: form.factura_url || null,
        ocr_resultado: ocr || {},
        notas: form.notas.trim() || null,
      };
      const r = await fetch(`${API_BASE}/turismo/combustible/cargas`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) {
        onError?.(d.detail?.message || d.detail || 'Error al guardar');
        return;
      }
      onSuccess?.(`Carga registrada: ${form.galones} gal · $${Number(form.total_pagado).toLocaleString('es-CO')}`);
    } catch (e) {
      onError?.('Error de red');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={typeof window !== 'undefined' && window.innerWidth < 600}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <LocalGasStationIcon sx={{ color: '#dc2626' }} />
        Nueva carga de combustible
      </DialogTitle>
      <DialogContent>
        {/* Paso 1: Embarcación */}
        <TextField fullWidth select size="small" label="Embarcación *"
          value={form.embarcacion_id} onChange={set('embarcacion_id')}
          sx={{ mt: 1, mb: 2 }}
        >
          {embarcaciones.length === 0 && (
            <MenuItem value="" disabled>No tienes embarcaciones asignadas</MenuItem>
          )}
          {embarcaciones.map(e => (
            <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
          ))}
        </TextField>

        {/* Paso 2: Foto factura */}
        <Box sx={{ p: 1.5, borderRadius: 2, border: '1px dashed #cbd5e1', bgcolor: '#fef2f2', mb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#dc2626', mb: 1, textTransform: 'uppercase' }}>
            Foto de la factura / ticket
          </Typography>
          {form.factura_url ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ color: '#16a34a' }} />
              <Typography sx={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {facturaNombre}
              </Typography>
              <Button size="small"
                onClick={() => {
                  setForm(p => ({ ...p, factura_url: '' })); setFacturaNombre(''); setOcr(null);
                }}
                sx={{ textTransform: 'none', color: '#dc2626', fontSize: 11 }}
              >
                Quitar
              </Button>
            </Box>
          ) : (
            <Button component="label" fullWidth variant="contained" size="large"
              disabled={subiendo}
              startIcon={<CameraAltIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                py: 1.5,
              }}
            >
              {subiendo ? 'Subiendo…' : 'Tomar foto / adjuntar factura'}
              <input hidden type="file" accept="image/*,application/pdf"
                capture="environment" onChange={subirFactura} />
            </Button>
          )}
          {analizando && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} sx={{ color: '#dc2626' }} />
              <Typography sx={{ fontSize: 11, color: '#dc2626' }}>
                Claude Vision analizando factura…
              </Typography>
            </Box>
          )}
          {ocr && ocr.ok && (
            <Box sx={{ mt: 1 }}>
              <Chip label={`✨ IA detectó · confianza ${Math.round(ocr.confianza * 100)}%`} size="small"
                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: 10 }}
              />
              {ocr.estacion_nombre && !ocr.estacion_id_match && (
                <Typography sx={{ fontSize: 11, color: '#92400e', mt: 0.5 }}>
                  Estación detectada: "{ocr.estacion_nombre}" — no coincide con ninguna registrada.
                </Typography>
              )}
            </Box>
          )}
          {ocr && !ocr.ok && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: 11 }}>
              IA no pudo leer bien. Ingresa los datos manualmente.
            </Alert>
          )}
        </Box>

        {/* Paso 3: Datos editables */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
          Datos de la carga
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" select label="Estación"
              value={form.estacion_id} onChange={set('estacion_id')}
            >
              <MenuItem value="">(sin especificar)</MenuItem>
              {estaciones.map(e => (
                <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" select label="Tipo"
              value={form.tipo_combustible} onChange={set('tipo_combustible')}
            >
              <MenuItem value="gasolina">Gasolina</MenuItem>
              <MenuItem value="diesel">Diesel</MenuItem>
              <MenuItem value="mezcla_2t">Mezcla 2T</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Galones *"
              value={form.galones} onChange={set('galones')}
              inputProps={{ step: 0.001, min: 0 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="$/galón *"
              value={form.precio_por_galon} onChange={set('precio_por_galon')}
              inputProps={{ step: 100, min: 0 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" type="number" label="Total *"
              value={form.total_pagado} onChange={set('total_pagado')}
              inputProps={{ step: 100, min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="# Ticket"
              value={form.referencia_ticket} onChange={set('referencia_ticket')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" type="datetime-local" label="Fecha/hora"
              InputLabelProps={{ shrink: true }}
              value={form.fecha_carga} onChange={set('fecha_carga')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2} label="Notas"
              value={form.notas} onChange={set('notas')}
            />
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 2, fontSize: 11 }}>
          Esta carga queda registrada <strong>a crédito</strong> con la estación.
          El contador la marcará como pagada al liquidar la factura mensual.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={!puedeGuardar || saving}
          sx={{
            textTransform: 'none', fontWeight: 700,
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          }}
        >
          {saving ? 'Guardando…' : 'Guardar carga'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
